import { EventEmitter, Injectable } from '@angular/core';
import { Params, Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CLIENT_ID } from 'projects/se-modules-platform/src/lib/constants';
import { Observable } from 'rxjs';

import { IEcgData } from '../models/iecgdata';
import { AppConfig } from '../../app/app.config';

class QueryParams {
    pairingToken: string = '';
    tokenEndPoint: string = '';
}

export class BaseStudiesLoaderService {
    // events

    streamsReady = new EventEmitter<Array<Observable<IEcgData>>>();

    // constructor

    constructor() {
        // This is intentional
    }

    // public functions

    retrieveStreams() {
        // This is intentional
    }
}

@Injectable({ providedIn: 'root' })
export class MockStudiesLoaderService extends BaseStudiesLoaderService {
    // data members

    private _streams = new Array<Observable<IEcgData>>();

    // constructor

    constructor(
        private router: Router,
        private httpClient: HttpClient,
        private appConfig: AppConfig,
    ) {
        super();
    }

    // public functions

    retrieveStreams() {
        this.router.routerState.root.queryParams.subscribe((params) => {
            if (params['local'] !== null && params['local'] == 'true') {
                let studies: string[] = this.appConfig.getValue('mockStudies').split(',');

                studies.forEach((study) => {
                    const studyName = `${study}.json`;

                    this._streams.push(this.httpClient.get<IEcgData>('./assets/mock/' + studyName));
                });
                this.streamsReady.emit(this._streams);
            }
        });
    }
}

@Injectable()
export class TrustedStudiesLoaderService extends BaseStudiesLoaderService {
    // data members

    private _streams = new Array<Observable<IEcgData>>();
    private tokenQueryParams: QueryParams = new QueryParams();
    private tokenBody: string = '';
    private tokenOptions;

    constructor(
        private router: Router,
        private httpClient: HttpClient,
        private appConfig: AppConfig,
    ) {
        super();
        this.tokenOptions = this.buildTokenOptions();
    }

    private _token: string = '';

    // constructor

    get token(): string {
        return this._token;
    }

    // public functions

    retrieveStreams() {
        this.router.routerState.root.queryParams.subscribe((params) => {
            this.tokenQueryParams = this.extractTokenQueryParams(params);
            if (!this.validatePairingToken(this.tokenQueryParams.pairingToken)) return;

            this.tokenBody = this.buildTokenBody(this.tokenQueryParams.pairingToken);
            this.tokenOptions = this.buildTokenOptions();

            this.getToken().subscribe((token) => {
                this._token = token.access_token;
                this.retrieveStudies(params, this._token);
            });
        });
    }

    // private functions

    private getToken(): Observable<any> {
        return this.httpClient.post<any>(this.tokenQueryParams.tokenEndPoint, this.tokenBody, this.tokenOptions);
    }

    private retrieveStudies(params: Params, token: string) {
        let queryParams = this.buildStudiesQueryParams(params);
        if (queryParams.ecgs == null) return;

        queryParams.ecgs.forEach((_: any, index: number) => {
            let data = this.buildStudiesBody(queryParams, index);
            let options = this.buildStudiesOptions(token);

            this._streams.push(this.httpClient.post<IEcgData>(queryParams.ecgEndPoint, data, options));
        });

        this.streamsReady.emit(this._streams);
    }

    private extractTokenQueryParams(params: { [x: string]: string }) {
        let queryParams = new QueryParams();
        queryParams.pairingToken = params['pairingToken'];
        queryParams.tokenEndPoint = this.appConfig.getValue('fakeEndPoint')
            ? this.appConfig.getValue('tokenEndPoint')
            : this.buildTokenEndPoint();

        return queryParams;
    }

    private validatePairingToken(pairingToken: string) {
        return pairingToken != null;
    }

    private buildTokenOptions() {
        let headers = new HttpHeaders({
            'Content-Type': 'application/json',
        });

        return { headers: headers };
    }

    private buildTokenBody(pairingToken: string) {
        const bodyData = {
            pairingToken: pairingToken,
            grant_type: 'client_credentials',
            client_id: CLIENT_ID,
        };

        return (
            'grant_type=' +
            bodyData.grant_type +
            '&client_id=' +
            bodyData.client_id +
            '&pairingToken=' +
            bodyData.pairingToken
        );
    }

    private buildStudiesQueryParams(params: { [x: string]: string }) {
        let ecgEndPoint = this.appConfig.getValue('fakeEndPoint')
            ? this.appConfig.getValue('ecgEndPoint')
            : this.buildEcgEndPoint();
        let ecgSOPInstanceUIDs = params['ecgSOPInstanceUIDs'];

        let ecgs: string[] = [];
        let patients: string[];
        let patientNames = params['patients'];
        if (patientNames == null) patients = [];
        else patients = patientNames.split(';').filter(Boolean);

        if (ecgSOPInstanceUIDs != null) ecgs = ecgSOPInstanceUIDs.split(';').filter(Boolean);

        return { ecgEndPoint: ecgEndPoint, ecgs: ecgs, patients: patients };
    }

    private buildStudiesOptions(token: string) {
        let headers = new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
        });

        return { headers: headers };
    }

    private buildStudiesBody(queryData: { ecgEndPoint?: any; ecgs: string[]; patients?: any }, index: number) {
        return JSON.stringify({ ecgSOPInstanceUID: queryData.ecgs[index].replace(';', '') });
    }

    private buildTokenEndPoint() {
        return location.origin + '/Estensa.Rest/oauth2/login/frame';
    }

    private buildEcgEndPoint() {
        return location.origin + '/Estensa.Rest/Client/api/ECG/Post';
    }

}

